// The weekly trend's data and layout rules, kept out of Trend.tsx so `node --test` can assert
// them: which weeks get a date under them, which get a count on top, and what the hover says.
//
// The chart used to print a date and a "N replies · x%" line under every bar. That reads at
// three weeks and turns into a smear at twenty-six, where each bar has ~27px and the date alone
// needs ~40. So labels are thinned to every Nth bar, N computed from the room each bar has, and
// the replies moved into each bar's hover where there is no width to fight over.

import type { ActivityWeek } from '../api/types.ts';
import { formatCount } from '../lib/format.ts';

export interface WeekPoint {
  week: string;
  sends: number;
  replies: number;
  accounts: number;
}

// One row per week across every strategist in scope; the page's own filter decides who is in
// scope before this is called.
export function foldWeeks(weeks: ActivityWeek[]): WeekPoint[] {
  const byWeek = new Map<string, WeekPoint>();
  weeks.forEach(week => {
    const point = byWeek.get(week.week)
      ?? { week: week.week, sends: 0, replies: 0, accounts: 0 };
    point.sends += week.sends;
    point.replies += week.replies;
    point.accounts += week.accounts_worked;
    byWeek.set(week.week, point);
  });
  return [...byWeek.values()].sort((first, second) => first.week.localeCompare(second.week));
}

// The week's own reply rate, or null when there have not been enough sends in it for a
// percentage to mean anything. Same rule as everywhere else on this board, applied per week
// because a week is a much smaller denominator than a window.
export function weekRate(point: WeekPoint, minDenominator: number): string | null {
  if (point.sends < minDenominator) return null;
  return `${Math.round((point.replies / point.sends) * 100)}%`;
}

// "2026-08-24" reads as "24 Aug" on an axis. The year is on the section heading already.
const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                     'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export function weekLabel(week: string): string {
  const day = new Date(week + 'T00:00:00Z');
  if (Number.isNaN(day.getTime())) return week;
  return `${day.getUTCDate()} ${MONTH_NAMES[day.getUTCMonth()]}`;
}

// Estimated rendered width of a monospace label. The chart's text is all `--font-mono`, where
// a glyph is ~0.6em wide, so a character count is a good enough measure without touching the
// DOM. The padding is the minimum clear space between two neighbouring labels.
const MONO_CHAR_EM = 0.6;
const LABEL_GAP_PX = 8;

export function monoTextWidth(text: string, fontSizePx: number): number {
  return text.length * fontSizePx * MONO_CHAR_EM + LABEL_GAP_PX;
}

// Show one label every `stride` bars: the smallest whole number of slots a label fits into.
// One means every bar gets its label.
export function labelStride(slotWidth: number, labelWidth: number): number {
  if (slotWidth <= 0) return 1;
  return Math.max(1, Math.ceil(labelWidth / slotWidth));
}

// Whether bar `index` of `count` carries a label at this stride. Counted back from the last
// bar, so the most recent week -- the one a reader looks for first -- is always labelled.
export function showsLabel(index: number, count: number, stride: number): boolean {
  return (count - 1 - index) % stride === 0;
}

// Which bars carry their send count on top. Every bar when each count fits its own slot.
// Otherwise a thinned set of counts lands on short bars beside tall ones and reads as belonging
// to the neighbour, so only two are kept: the latest week, and the peak when it is far enough
// from the latest not to collide with it. Every other week's count is in its hover.
export function valueLabelIndices(sends: number[], stride: number): Set<number> {
  const count = sends.length;
  if (!count) return new Set();
  if (stride <= 1) return new Set(sends.map((_, index) => index));

  const last = count - 1;
  let peak = 0;
  sends.forEach((value, index) => {
    if (value > sends[peak]!) peak = index;
  });
  return last - peak >= stride ? new Set([peak, last]) : new Set([last]);
}

// The hover text for one bar: the week, its sends, and the replies and rate that used to sit
// under the axis.
export function weekTooltip(point: WeekPoint, minDenominator: number): string {
  const rate = weekRate(point, minDenominator);
  const replyWord = point.replies === 1 ? 'reply' : 'replies';
  const accountWord = point.accounts === 1 ? 'account' : 'accounts';
  return `Week of ${weekLabel(point.week)}\n`
    + `${formatCount(point.sends)} sent\n`
    + `${formatCount(point.replies)} ${replyWord}${rate ? ` · ${rate} reply rate` : ''}\n`
    + `${formatCount(point.accounts)} ${accountWord} worked`;
}
